import React from 'react';
import { Navigate } from 'react-router-dom';
import { useGame } from '../../contexts/GameContext';
import { useAuth } from '../../contexts/AuthContext';
import { Container, Row, Col } from 'react-bootstrap';

interface GameRouteProps {
  children: React.ReactNode;
  requireGameStatusName?: string[];
}

// Helper function to convert numeric status to string
function convertNumericStatusToString(status: number): 'Created' | 'WaitingForPlayers' | 'InProgress' | 'Completed' | 'Cancelled' {
  switch (status) {
    case 0: return 'Created';
    case 1: return 'WaitingForPlayers';
    case 2: return 'InProgress';
    case 3: return 'Completed';
    case 4: return 'Cancelled';
    default: return 'Created';
  }
}

// Helper function to convert string status to numeric
function getNumericStatusFromName(statusName: string): number {
  switch (statusName) {
    case 'Created': return 0;
    case 'WaitingForPlayers': return 1;
    case 'InProgress': return 2;
    case 'Completed': return 3;
    case 'Cancelled': return 4;
    default: return 0;
  }
}

export default function GameRoute({ 
  children, 
  requireGameStatusName = [] 
}: GameRouteProps) {
  const { isInGame, gameSession, isLoading } = useGame();
  const { isAuthenticated } = useAuth();
  
  // Enhanced debug logging
  console.log('GameRoute: Initial render state', {
    isInGame,
    isAuthenticated,
    isLoading,
    requireGameStatusName,
    gameSessionExists: !!gameSession,
    gameSessionId: gameSession?.id,
    gameSessionStatus: gameSession?.status,
    gameSessionStatusName: gameSession?.statusName,
    gameSessionStatusType: typeof gameSession?.status
  });

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Show loading while game state is being determined
  if (isLoading) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6} className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading game...</span>
            </div>
            <p className="mt-2 text-muted">Loading game...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  // Redirect to home if not in a game
  if (!isInGame || !gameSession) {
    console.log('GameRoute: Not in a game or no gameSession, redirecting to home');
    return <Navigate to="/" replace />;
  }
  
  // Debug logging
  console.log('GameRoute: Current gameSession:', {
    id: gameSession.id,
    statusName: gameSession.statusName,
    status: gameSession.status
  });

  // Check if requireGameStatusName is empty or includes current status
  const currentStatusName = gameSession.statusName || convertNumericStatusToString(gameSession.status);
  
  // Convert status to number if it's a string
  const statusAsNumber = typeof gameSession.status === 'string' 
    ? getNumericStatusFromName(gameSession.status as any) 
    : gameSession.status;
  
  console.log('GameRoute: Checking route requirements', {
    requireStatusNames: requireGameStatusName,
    currentStatusName,
    currentStatus: gameSession.status,
    statusAsNumber,
    statusType: typeof gameSession.status
  });
  
  // If we have required status names and current status doesn't match, redirect
  // Debug the status check in detail
  console.log('GameRoute: Status check details', {
    requireGameStatusNameLength: requireGameStatusName.length,
    requireGameStatusName,
    currentStatusName,
    statusMatch: requireGameStatusName.includes(currentStatusName),
    condition: requireGameStatusName.length > 0 && !requireGameStatusName.includes(currentStatusName)
  });
  
  if (requireGameStatusName.length > 0 && !requireGameStatusName.includes(currentStatusName)) {
    console.log('GameRoute: Status mismatch, redirecting');
    
    // Navigate based on current game status
    switch (statusAsNumber) {
      case 0: // Created
      case 1: // WaitingForPlayers
        return <Navigate to="/lobby" replace />;
      case 2: // InProgress
        return <Navigate to="/play" replace />;
      case 3: // Completed
        return <Navigate to="/results" replace />;
      default:
        return <Navigate to="/" replace />;
    }
  }
  
  // If we reach here, the status matches the requirements, so render the children
  console.log('GameRoute: Status matches requirements, rendering children');
  return children;
}
